import type {
  FiQuestionnaireSystemTaskData,
  RequestForDocumentTaskData,
  TaskInputRequest,
  User
} from '@gcv/shared';
import {
  IANATimezones,
  OrganizationRoleResolver,
  TaskCategory,
  TaskInternalExternal,
  TaskInternalType,
  TaskStatus,
  TaskUser
} from '@gcv/shared';
import { inject, injectable } from 'inversify';
import { action, makeAutoObservable, observe } from 'mobx';

import { lookupQuestionnaireFrequencyLabel } from 'domain/consts/due-diligence';
import type { TimePeriod } from 'stores/AppViewStateStore';
import { AppPage, AppViewStateStore, isEqualTimePeriod } from 'stores/AppViewStateStore';
import { FiBankStore } from 'stores/FiBankStore';
import { FiQuestionnaireStore } from 'stores/FiQuestionnaireStore';
import { NotificationStore } from 'stores/NotificationStore';
import { SnackbarStore } from 'stores/SnackBarStore';
import { UserStore } from 'stores/UserStore';
import type { FilterListChild, FilterListItem } from 'ui';
import palette from 'ui/theme/palette';
import { getDateRange } from 'util/dateRange.util';
import { DateTimeHelpers } from 'util/dateTime.util';
import { filterUtil } from 'util/filter.util';
import { getTaskStatusText, removeUuid } from 'util/format.util';
import { searchUtil } from 'util/search.util';
import { capitalizeVariables } from 'util/strings.util';
import type { TaskAssignee } from './components/tasks-modal.model';
import { TaskAssigneeType } from './components/tasks-modal.model';
import { FiTasksModalRepo } from './components/tasks-modal.repo';
import type { DecoratedTask, TaskDetails, TaskDrawerField, TaskRow } from './tasks.model';
import { hasSubtasks, isGroupedTask, TaskRowType } from './tasks.model';
import type { PM } from './tasks.repo';
import { FiTasksRepo } from './tasks.repo';
import { COMMENT_TASK_TYPES, getGroupedAccountNames, getGroupedAssigneeName, getGroupedStatus } from './util';

const mapTaskRowType = (internalType: TaskInternalType | undefined): TaskRow['type'] => {
  if (internalType === TaskInternalType.QUESTIONNAIRE) return TaskRowType.Questionnaire;
  if (internalType === TaskInternalType.REVISE_QUESTIONNAIRE) return TaskRowType.QuestionnaireRevision;
  return TaskRowType.Task;
};

export interface VM {
  assignees: TaskAssignee[];
  bankId: string;
  currentTask: TaskDetails | null;
  internalFilteredTasks: TaskRow[];
  externalFilteredTasks: TaskRow[];
  filters: FilterListChild[];
  isBankAdmin: boolean;
  isCompleteModalOpen: boolean;
  isCreateModalOpen: boolean;
  isDeleteModalOpen: boolean;
  isDetailsDrawerOpen: boolean;
  isQuestionnaireDrawerOpen: boolean;
  isReassignModalOpen: boolean;
  isLoading: boolean;
  isFiltering: boolean;
  loadingAssignees: boolean;
  searchTerm: string;
  selectedSubtaskIds: string[];
  tasks: TaskRow[];
  timezone: IANATimezones;
  showAutogen: boolean;
}

@injectable()
export class FiTasksPresenter {
  @inject(FiTasksRepo)
  private repo: FiTasksRepo;

  @inject(AppViewStateStore)
  private appViewStore: AppViewStateStore;

  @inject(FiBankStore)
  private bankStore: FiBankStore;

  @inject(SnackbarStore)
  private snackbarStore: SnackbarStore;

  @inject(UserStore)
  private userStore: UserStore;

  @inject(OrganizationRoleResolver)
  private resolver: OrganizationRoleResolver;

  @inject(NotificationStore)
  private notificationStore: NotificationStore;

  @inject(FiQuestionnaireStore)
  private fiQuestionnaireStore: FiQuestionnaireStore;

  @inject(FiTasksModalRepo)
  private tasksModalRepo: FiTasksModalRepo;

  public viewModel: VM = {
    assignees: [],
    bankId: '',
    currentTask: null,
    filters: [],
    internalFilteredTasks: [],
    externalFilteredTasks: [],
    isBankAdmin: false,
    isCompleteModalOpen: false,
    isCreateModalOpen: false,
    isDeleteModalOpen: false,
    isDetailsDrawerOpen: false,
    isQuestionnaireDrawerOpen: false,
    isReassignModalOpen: false,
    isLoading: false,
    isFiltering: true,
    loadingAssignees: false,
    searchTerm: '',
    selectedSubtaskIds: [],
    tasks: [],
    timezone: IANATimezones.America_NewYork,
    showAutogen: false
  };

  public externalDrawerFields = (): Record<string, TaskDrawerField> => {
    const composeStatusLabel = (task: TaskDetails | null): string => {
      // The UI design wants the past participle of "complete". The enum values are grammatically
      // inconsistent: COMPLETE should follow the same pattern as CANCELLED, FAILED, and DELETED.
      return task?.status === TaskStatus.COMPLETE
        ? 'Completed'
        : capitalizeVariables(task?.status ?? '') ?? '--';
    };

    return {
      status: {
        label: 'Status',
        value: composeStatusLabel(this.viewModel.currentTask),
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 33,
        color: this.viewModel.currentTask?.status === TaskStatus.COMPLETE ? palette.primary.main : undefined
      },
      dueDate: {
        label: 'Due Date',
        value: DateTimeHelpers.formatISOToTableDateString(
          this.viewModel.currentTask?.dueDate,
          this.bankStore.bank.iana_timezone
        ),
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 33
      },
      createdDate: {
        label: 'Sent On',
        value: DateTimeHelpers.formatISOToTableDateString(
          this.viewModel.currentTask?.createdDate,
          this.bankStore.bank.iana_timezone
        ),
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 33
      },
      requirementName: {
        label: 'Requirement',
        value: (this.viewModel.currentTask?.data as RequestForDocumentTaskData)?.requirement_name ?? '--',
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 33
      },
      account: {
        label: 'Account',
        value: this.viewModel.currentTask?.account ?? '--',
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 100
      },
      assignee: {
        label: 'Assignee',
        value: this.viewModel.currentTask?.assignee ?? '--',
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 33
      },
      completedBy: {
        label: 'Completed By',
        value: this.viewModel.currentTask?.completedBy ?? '--',
        state: [TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 33
      },
      completedOn: {
        label: 'Completed On',
        value: DateTimeHelpers.formatISOToTableDateString(
          this.viewModel.currentTask?.completedDate,
          this.bankStore.bank.iana_timezone
        ),
        state: [TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 33
      },
      description: {
        label: 'Task Description',
        value: this.viewModel.currentTask ? removeUuid(this.viewModel.currentTask?.description) : '--',
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 100
      },
      lastReminder: {
        label: 'Last Reminder Sent On',
        value: DateTimeHelpers.formatISOToTableDateString(
          this.viewModel.currentTask?.lastReminderDate,
          this.bankStore.bank.iana_timezone
        ),
        state: [TaskStatus.OPEN],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 50
      },
      nextReminder: {
        label: 'Next Reminder Scheduled For',
        value: DateTimeHelpers.formatISOToTableDateString(
          this.viewModel.currentTask?.nextReminderDate,
          this.bankStore.bank.iana_timezone
        ),
        state: [TaskStatus.OPEN],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 50
      },
      createdBy: {
        label: 'Created By',
        value: this.viewModel.currentTask?.createdBy ?? '--',
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 33
      },
      completedNote: {
        label: 'Completed Note',
        value: this.viewModel.currentTask?.completedNote?.length
          ? this.viewModel.currentTask?.completedNote
          : '--',
        state: [TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 100
      }
    };
  };

  public taskDrawerFields = (): Record<string, TaskDrawerField> => {
    const composeStatusLabel = (task: TaskDetails | null): string => {
      // The UI design wants the past participle of "complete". The enum values are grammatically
      // inconsistent: COMPLETE should follow the same pattern as CANCELLED, FAILED, and DELETED.
      return task?.status === TaskStatus.COMPLETE
        ? 'Completed'
        : capitalizeVariables(task?.status ?? '') ?? '--';
    };

    const getCompletedHowValue = (): string => {
      if (this.viewModel.currentTask?.internalType !== TaskInternalType.SEEDLING_QUESTIONNAIRE_COMPLETE) {
        return '--';
      }

      const systemCompleted = this.viewModel.currentTask?.groupedTasks?.[0]?.system_completed;
      if (systemCompleted === false) {
        return 'Manually Completed';
      }

      return 'System Completed';
    };

    return {
      name: {
        label: 'Task Name',
        value: this.viewModel.currentTask?.name ?? '--',
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 100
      },
      status: {
        label: 'Status',
        value: composeStatusLabel(this.viewModel.currentTask),
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 33,
        color: this.viewModel.currentTask?.status === TaskStatus.COMPLETE ? palette.primary.main : undefined
      },
      assignee: {
        label: 'Assignee',
        value: this.viewModel.currentTask?.assignee ?? '--',
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 33
      },
      completedBy: {
        label: 'Completed By',
        value: this.viewModel.currentTask?.completedBy ?? '--',
        state: [TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 33
      },
      description: {
        label: 'Task Description',
        value: this.viewModel.currentTask ? removeUuid(this.viewModel.currentTask?.description) : '--',
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 100
      },
      lastReminder: {
        label: 'Last Reminder Sent On',
        value: DateTimeHelpers.formatISOToTableDateString(
          this.viewModel.currentTask?.lastReminderDate,
          this.bankStore.bank.iana_timezone
        ),
        state: [TaskStatus.OPEN],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 50
      },
      nextReminder: {
        label: 'Next Reminder Scheduled For',
        value: DateTimeHelpers.formatISOToTableDateString(
          this.viewModel.currentTask?.nextReminderDate,
          this.bankStore.bank.iana_timezone
        ),
        state: [TaskStatus.OPEN],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 50
      },
      account: {
        label: 'Account',
        value: this.viewModel.currentTask?.account ?? '--',
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 100
      },
      associated: {
        label: 'Associated With',
        value:
          this.viewModel.currentTask?.associatedWith === 'fincen_reports'
            ? 'FinCEN Reports'
            : capitalizeVariables(this.viewModel.currentTask?.associatedWith ?? '') ?? '--',
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 100
      },
      dueDate: {
        label: 'Due Date',
        value:
          DateTimeHelpers.formatISOToTableDateString(
            this.viewModel.currentTask?.dueDate,
            this.bankStore.bank.iana_timezone
          ) ?? '--',
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 33
      },
      createdDate: {
        label: 'Created',
        value:
          DateTimeHelpers.formatISOToTableDateString(
            this.viewModel.currentTask?.createdDate,
            this.bankStore.bank.iana_timezone
          ) ?? '--',
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 33
      },
      createdBy: {
        label: 'Created By',
        value: this.viewModel.currentTask?.createdBy ?? '--',
        state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 33
      },
      completedDate: {
        label: 'Completed On',
        value:
          DateTimeHelpers.formatISOToTableDateString(
            this.viewModel.currentTask?.completedDate ?? '',
            this.bankStore.bank.iana_timezone
          ) ?? '--',
        state: [TaskStatus.COMPLETE],
        type: [TaskCategory.USER, TaskCategory.SYSTEM],
        width: 33
      },
      completedHow: {
        label: 'Completed How',
        value: getCompletedHowValue(),
        state: [TaskStatus.COMPLETE],
        type: [TaskCategory.SYSTEM],
        width: 33
      },
      note: {
        label: 'Completion Note',
        value: this.viewModel.currentTask?.completedNote?.length
          ? this.viewModel.currentTask?.completedNote
          : '--',
        state: [TaskStatus.COMPLETE],
        type: [TaskCategory.USER],
        width: 100
      },
      systemNote: {
        label: 'Completion Note',
        value: this.viewModel.currentTask?.completedNote?.length
          ? this.viewModel.currentTask?.completedNote
          : '--',
        state: [TaskStatus.COMPLETE],
        type: [TaskCategory.SYSTEM],
        width: 100
      }
    };
  };

  constructor() {
    makeAutoObservable(this);
  }

  private updateViewModel = action((viewModel: Partial<VM>) => {
    this.viewModel = { ...this.viewModel, ...viewModel };
  });

  private mapTaskToTaskRow = (decoratedTask: DecoratedTask): TaskRow => {
    const tasks = decoratedTask.tasks;

    const groupedAssignee = decoratedTask.isGrouped
      ? getGroupedAssigneeName(this.bankStore.bank.groups, tasks)
      : null;
    const assignee = groupedAssignee
      ? groupedAssignee.label
      : this.repo.getAssigneeName(
          tasks[0].assigned_users,
          tasks[0].assigned_group_ids,
          tasks[0].assigned_groups
        );
    const assigneeNames = groupedAssignee ? groupedAssignee.uniqueNames : [assignee];
    const createdByUser =
      tasks[0].created_by_user && tasks[0].created_by_user === TaskUser.AUTO_GENERATED_USER
        ? 'Auto Generated'
        : tasks[0].created_by_user && tasks[0].created_by_user.firstName && tasks[0].created_by_user.lastName
        ? `${tasks[0].created_by_user.firstName} ${tasks[0].created_by_user.lastName}`
        : '';

    const template_id = (tasks[0].data as FiQuestionnaireSystemTaskData)?.questionnaire_fi?.template_id;
    const is_managed_template = this.fiQuestionnaireStore.allQuestionnaires[template_id]?.is_managed_template;

    if (decoratedTask.isGrouped) {
      const completedDates = [...new Set(tasks.map((task) => task.completed_on))];
      const completedUsers = [
        ...new Set(
          tasks.map((task) => {
            const user = task.completed_by_user as User;

            if (!user) {
              return '--';
            }

            return user.firstName + ' ' + user.lastName;
          })
        )
      ];
      const completedOn = DateTimeHelpers.formatISOToTableDateString(
        completedDates.sort().reverse()[0] ?? '',
        this.bankStore.bank.iana_timezone
      );
      const completedBy = completedUsers.length > 1 ? 'Multiple Users' : completedUsers[0];
      const completedByUser =
        completedBy && completedBy === TaskUser.AUTO_GENERATED_USER ? 'Autocompleted' : completedBy;
      const reoccuring = (tasks[0]?.data as FiQuestionnaireSystemTaskData)?.questionnaire_fi?.reoccurring;

      const frequency = reoccuring
        ? lookupQuestionnaireFrequencyLabel(
            (tasks[0].data as FiQuestionnaireSystemTaskData)?.questionnaire_fi?.frequency
          )
        : 'One-Time';
      const openTasks = tasks.filter(
        (t) => t.status === TaskStatus.OPEN || t.status === TaskStatus.PROCESSING
      );
      const firstOpenTask = openTasks[0];
      let account = '--';
      if (tasks.length > 1) {
        account = 'Multiple Accounts';
      } else if (tasks[0].assigned_org.orgType === 'bank') {
        account = tasks[0].account?.name ?? '--';
      } else {
        account = tasks[0].assigned_org?.name;
      }
      const accountNames = getGroupedAccountNames(tasks);
      return {
        account: account,
        assignee: assignee,
        associatedWith: tasks[0].associated_with ?? '--',
        completed: completedOn ?? '--',
        completedBy: completedByUser ?? '--',
        created: DateTimeHelpers.formatISOToTableDateString(
          tasks[0].date_created,
          this.bankStore.bank.iana_timezone
        ),
        createdBy: createdByUser,
        dueDate: firstOpenTask?.process_date ?? tasks[0].process_date,
        id: decoratedTask.groupKey,
        taskName: tasks[0].title ?? '--',
        createdTimestamp: new Date(tasks[0].date_created).getTime(),
        completedTimestamp: tasks[0].completed_on ? new Date(tasks[0].completed_on).getTime() : undefined,
        groups: tasks[0].assigned_group_ids ?? [],
        assigneeNames: assigneeNames,
        accountNames: accountNames,
        status: this.getGroupedStatus(tasks),
        sentOn: DateTimeHelpers.formatISOToTableDateString(
          tasks[0].date_created,
          this.bankStore.bank.iana_timezone
        ),
        frequency: frequency,
        type: mapTaskRowType(tasks[0].internal_type),
        is_managed_template
      };
    } else {
      let account = '--';
      if (tasks[0].assigned_org.orgType === 'bank') {
        account = tasks[0].account?.name ?? '--';
      } else {
        account = tasks[0].assigned_org?.name;
      }
      const completedUser =
        tasks[0].completed_by_user &&
        (tasks[0].completed_by_user as User).firstName + ' ' + (tasks[0].completed_by_user as User).lastName;
      const reoccuring = (tasks[0]?.data as FiQuestionnaireSystemTaskData)?.questionnaire_fi?.reoccurring;
      const frequency = reoccuring
        ? lookupQuestionnaireFrequencyLabel(
            (tasks[0].data as FiQuestionnaireSystemTaskData)?.questionnaire_fi?.frequency
          )
        : 'One-Time';
      return {
        account: account ? account : '--',
        assignee: assignee,
        associatedWith: tasks[0].associated_with ?? '--',
        completed: DateTimeHelpers.formatISOToTableDateString(
          tasks[0].completed_on ?? '',
          this.bankStore.bank.iana_timezone
        ),
        completedBy: completedUser ? completedUser : '--',
        created: DateTimeHelpers.formatISOToTableDateString(
          tasks[0].date_created,
          this.bankStore.bank.iana_timezone
        ),
        createdBy: createdByUser,
        dueDate: tasks[0].process_date,
        id: tasks[0].id,
        taskName: tasks[0].title ?? '--',
        createdTimestamp: new Date(tasks[0].date_created).getTime(),
        completedTimestamp: tasks[0].completed_on ? new Date(tasks[0].completed_on).getTime() : undefined,
        groups: tasks[0].assigned_group_ids ?? [],
        assigneeNames: assigneeNames,
        accountNames: [account],
        status: tasks[0].status ?? '--',
        sentOn: DateTimeHelpers.formatISOToTableDateString(
          tasks[0].date_created,
          this.bankStore.bank.iana_timezone
        ),
        frequency: frequency,
        type: mapTaskRowType(tasks[0].internal_type),
        is_managed_template
      };
    }
  };

  public load = action(
    async (taskDirection: TaskInternalExternal, keepSearch: boolean, currentTab: string) => {
      observe(this.repo, 'programmersModel', (obj) => {
        const programmersModel = obj.newValue as PM;
        const taskList: TaskRow[] = programmersModel.tasks.map((t) => this.mapTaskToTaskRow(t));
        this.viewModel.currentTask = programmersModel.currentTask;
        this.viewModel.tasks = taskList;
        this.viewModel.isLoading = programmersModel.isLoading;
      });

      const defaultTimeRange = this.appViewStore.tasksTimePeriod[currentTab].timeRange;
      const defaultStartDate = defaultTimeRange.start;
      const timezone = this.bankStore.bank.iana_timezone;
      const timeRange = !defaultStartDate
        ? getDateRange(this.appViewStore.tasksTimePeriod[currentTab].value, timezone)
        : defaultTimeRange;

      await this.repo.load(this.bankStore.bank.id, taskDirection, timeRange.start, timeRange.end, [
        TaskStatus.OPEN,
        TaskStatus.PROCESSING,
        TaskStatus.COMPLETE,
        TaskStatus.SUPPRESSED
      ]);

      this.viewModel.bankId = this.bankStore.bank.id;
      this.viewModel.timezone = timezone;
      this.viewModel.isBankAdmin =
        this.resolver.userHasRole(this.bankStore.bank.groups, this.userStore.user, 'bank_admin') ||
        this.resolver.userHasRole(this.bankStore.bank.groups, this.userStore.user, 'bank_primary_contact');
      this.viewModel.searchTerm = keepSearch ? this.appViewStore.tasksSearchTerm : '';

      this.refilterResults(taskDirection);
    }
  );

  public getGroupedStatus = getGroupedStatus;

  public showLoad = action((load: boolean) => {
    this.viewModel.isLoading = load;
  });

  public loadTask = async (taskId: string, isExternal = false) => {
    await this.repo.getTaskById(this.bankStore.bank.id, taskId);

    if (this.viewModel.currentTask?.isGrouped) {
      this.loadAllSubtaskAssignees(isExternal);
    }
  };

  public setTimePeriod = (data: TimePeriod, currentTab: string) => {
    if (!isEqualTimePeriod(data, this.appViewStore.tasksTimePeriod[currentTab])) {
      this.appViewStore.setTimePeriod(
        {
          timeRange: data.timeRange,
          dateRange: data.dateRange,
          value: data.value
        },
        AppPage.tasks,
        currentTab
      );
    }
  };

  public updateInternalFilteredTasks = action((filteredList: TaskRow[]) => {
    this.viewModel.internalFilteredTasks = filteredList;
  });

  public updateExternalFilteredTasks = action((filteredList: TaskRow[]) => {
    this.viewModel.externalFilteredTasks = filteredList;
  });

  public createNewTask = async (payload: TaskInputRequest) => {
    await this.repo.createNewTask(this.bankStore.bank.id, payload).catch(() => {
      this.snackbarStore.showErrorSnackbarMessage(
        'There was a problem creating the task. Please try again or contact our help desk!'
      );
    });
  };

  public toggleDetailsDrawer = (isOpen: boolean) => {
    this.updateViewModel({ isDetailsDrawerOpen: isOpen });
  };

  public toggleQuestionnaireDetailsDrawer = (isOpen: boolean) => {
    this.updateViewModel({ isQuestionnaireDrawerOpen: isOpen });
  };

  public toggleDeleteModal = (isOpen: boolean) => {
    this.updateViewModel({ isDeleteModalOpen: isOpen });
  };

  public toggleCompleteModal = (isOpen: boolean) => {
    this.updateViewModel({ isCompleteModalOpen: isOpen });
  };

  public isSupportUser = () => {
    return this.resolver.userHasRole(this.bankStore.bank.groups, this.userStore.user, 'gcv_customer_support');
  };

  public isGroupedTask = () => {
    if (!this.viewModel.currentTask) {
      return false;
    }

    const isGrouped = isGroupedTask(this.viewModel.currentTask);
    const hasMultiple = hasSubtasks(this.viewModel.currentTask);

    // Monitoring reviews always show the grouped view, even with a single subtask
    if (this.viewModel.currentTask.internalType === TaskInternalType.MONITORING_REVIEWS && isGrouped) {
      return true;
    }

    return isGrouped && hasMultiple;
  };

  public isOpenUserTask = () => {
    if (!this.viewModel.currentTask) {
      return false;
    }

    const isOpen = this.viewModel.currentTask.status === TaskStatus.OPEN;
    const isUser = this.viewModel.currentTask.taskCategory === TaskCategory.USER;

    return isOpen && isUser;
  };

  public isOpenSystemTask = () => {
    if (!this.viewModel.currentTask) {
      return false;
    }

    const isOpen = this.viewModel.currentTask.status === TaskStatus.OPEN;
    const isSystem = this.viewModel.currentTask.taskCategory === TaskCategory.SYSTEM;

    return isOpen && isSystem && !this.isGroupedTask();
  };

  public fieldShouldRender = (field: TaskDrawerField, isExternal: boolean, fieldName: string) => {
    if (!this.viewModel.currentTask || this.isGroupedTask()) {
      return false;
    }
    if (
      this.viewModel.currentTask.internalType !== TaskInternalType.DOCUMENTS &&
      fieldName === 'requirementName'
    ) {
      return false;
    }

    // Only show completedHow and systemNote for seedling questionnaire completed tasks
    if (fieldName === 'completedHow' || fieldName === 'systemNote') {
      return (
        this.viewModel.currentTask.internalType === TaskInternalType.SEEDLING_QUESTIONNAIRE_COMPLETE &&
        this.viewModel.currentTask.status === TaskStatus.COMPLETE
      );
    }

    return (
      this.viewModel.currentTask &&
      (field.state.includes(this.viewModel.currentTask.status) ||
        [TaskStatus.OPEN, TaskStatus.COMPLETE].every((s) => {
          return field.state.includes(s);
        })) &&
      (field.type.includes(this.viewModel.currentTask.taskCategory) ||
        [TaskCategory.USER, TaskCategory.SYSTEM].every((s) => {
          return field.type.includes(s);
        }))
    );
  };

  public showCreateTaskModal = (isOpen: boolean) => {
    this.updateViewModel({ isCreateModalOpen: isOpen });
  };

  public refilterResults = (currentTab: TaskInternalExternal) => {
    this.viewModel.isFiltering = true;

    const currentFilters = this.viewModel.filters.map((s) => {
      const selectedValue = s.value === 'assignee_none' ? '--' : s.value === 'account_none' ? '--' : s.value;

      return {
        ...s,
        value: selectedValue
      };
    });

    if (currentTab === TaskInternalExternal.INTERNAL) {
      filterUtil(this.updateInternalFilteredTasks, this.viewModel.tasks, currentFilters);
      searchUtil(
        this.updateInternalFilteredTasks,
        this.viewModel.internalFilteredTasks,
        [
          'taskName',
          'assignee',
          'assigneeNames',
          'account',
          'accountNames',
          'associatedWith',
          'created',
          'createdBy',
          'dueDate'
        ],
        this.viewModel.searchTerm
      );
    } else {
      filterUtil(this.updateExternalFilteredTasks, this.viewModel.tasks, currentFilters);
      searchUtil(
        this.updateExternalFilteredTasks,
        this.viewModel.externalFilteredTasks,
        [
          'taskName',
          'assignee',
          'account',
          'accountNames',
          'associatedWith',
          'created',
          'createdBy',
          'dueDate'
        ],
        this.viewModel.searchTerm
      );
    }

    if (currentTab === TaskInternalExternal.INTERNAL) {
      this.filterAutogen(
        this.updateInternalFilteredTasks,
        this.viewModel.internalFilteredTasks,
        this.viewModel.showAutogen
      );
    }

    this.viewModel.isFiltering = false;
  };

  filterAutogen = (
    updateFilteredTasks: (filteredList: TaskRow[]) => void,
    filteredTasks: TaskRow[],
    showAutogen: boolean
  ) => {
    if (!showAutogen) {
      updateFilteredTasks(filteredTasks.filter((task) => task.createdBy !== 'Auto Generated'));
    }
  };

  public deleteCurrentTask = async (currentTab: TaskInternalExternal) => {
    this.updateViewModel({
      isDetailsDrawerOpen: false,
      isDeleteModalOpen: false
    });
    await this.repo.deleteCurrentTask(this.viewModel.bankId);
    await this.load(currentTab ? currentTab : TaskInternalExternal.INTERNAL, false, currentTab);
    this.refilterResults(currentTab);
    this.notificationStore.getAllUserNotifications(this.bankStore.bank.id, this.userStore.user.id);
  };

  public deleteCurrentQuestionnaire = async (currentTab: TaskInternalExternal) => {
    this.updateViewModel({
      isQuestionnaireDrawerOpen: false
    });
    await this.load(TaskInternalExternal.EXTERNAL, false, currentTab);
    this.refilterResults(currentTab);
  };

  public updateTask = async (
    status: TaskStatus,
    completed_note: string,
    currentTab: TaskInternalExternal
  ) => {
    this.viewModel.isLoading = true;
    this.updateViewModel({ isDetailsDrawerOpen: false, isCompleteModalOpen: false });
    await this.repo.updateCurrentTask(this.viewModel.bankId, status, completed_note, this.viewModel.timezone);
    this.refilterResults(currentTab);
    this.viewModel.isLoading = false;
  };

  public setSearchTerm = (searchTerm: string, currentTab: TaskInternalExternal) => {
    this.updateViewModel({ searchTerm });
    this.appViewStore.tasksSearchTerm = searchTerm;

    this.refilterResults(currentTab);
  };

  public setFilters = action((filters: FilterListChild[], currentTab: TaskInternalExternal) => {
    this.viewModel.filters = filters;
    this.appViewStore.tasksFilters[currentTab] = filters;

    this.refilterResults(currentTab);
  });

  public getGroupNames = (groupIds: string[]) => {
    const names: string[] = [];

    groupIds.forEach((id) => {
      names.push(this.bankStore.bank.groups.find((g) => g.id === id)?.name ?? '--');
    });

    return names;
  };

  public toggleReassignModal = (isOpen: boolean) => {
    this.viewModel.isReassignModalOpen = isOpen;
  };

  public setSelectedSubtasks = (ids: string[]) => {
    this.viewModel.selectedSubtaskIds = ids;
  };

  public loadAssignees = async (orgId = '', taskId?: string, isExternal = false) => {
    this.viewModel.loadingAssignees = true;

    try {
      const task = taskId ? this.viewModel.currentTask?.groupedTasks.find((t) => t.id === taskId) : undefined;

      const effectiveOrgId = isExternal ? task?.assigned_org?.id ?? orgId : orgId;

      const assignees = await this.tasksModalRepo.getAssignees({
        orgId: effectiveOrgId,
        taskCategory: task?.task_category ?? this.viewModel.currentTask?.taskCategory,
        originalGroupIds: [
          ...new Set([...(task?.assigned_group_ids ?? []), ...(task?.original_assigned_group_ids ?? [])])
        ],
        originalUserIds: task?.original_assigned_user_ids ?? task?.assigned_users?.map((u) => u.id!) ?? [],
        isExternal,
        isCommentTask: COMMENT_TASK_TYPES.includes(task?.internal_type)
      });
      this.viewModel.assignees = assignees;
    } finally {
      this.viewModel.loadingAssignees = false;
    }
  };

  public loadAllSubtaskAssignees = async (isExternal = false) => {
    this.viewModel.loadingAssignees = true;

    try {
      const allTasks = this.viewModel.currentTask?.groupedTasks ?? [];

      const originalGroupIds = [
        ...new Set(
          allTasks.flatMap((t) => [...(t.assigned_group_ids ?? []), ...(t.original_assigned_group_ids ?? [])])
        )
      ];

      const originalUserIds = [
        ...new Set(
          allTasks.flatMap((t) => t.original_assigned_user_ids ?? t.assigned_users?.map((u) => u.id!) ?? [])
        )
      ];

      const assignees = await this.tasksModalRepo.getAssignees({
        taskCategory: this.viewModel.currentTask?.taskCategory,
        originalGroupIds,
        originalUserIds,
        isExternal
      });

      this.viewModel.assignees = assignees;
    } finally {
      this.viewModel.loadingAssignees = false;
    }
  };

  public bulkReassignTasks = async (
    taskAssignees: Record<string, string>,
    currentTab: TaskInternalExternal
  ) => {
    const taskIds = Object.keys(taskAssignees);
    if (taskIds.length === 0) return;

    this.viewModel.isLoading = true;

    try {
      const assigneeGroups = new Map<string, string[]>();

      for (const [taskId, assigneeId] of Object.entries(taskAssignees)) {
        const group = assigneeGroups.get(assigneeId) ?? [];
        group.push(taskId);
        assigneeGroups.set(assigneeId, group);
      }

      for (const [assigneeId, groupedTaskIds] of assigneeGroups) {
        const assignee = this.viewModel.assignees.find((a) => a.id === assigneeId);
        if (!assignee) continue;

        const assignedUserIds = assignee.type === TaskAssigneeType.USER ? [assignee.id] : undefined;
        const assignedGroupIds = assignee.type === TaskAssigneeType.GROUP ? [assignee.id] : undefined;

        await this.repo.bulkUpdateTasks(
          this.viewModel.bankId,
          groupedTaskIds,
          assignedUserIds,
          assignedGroupIds
        );
      }

      this.viewModel.selectedSubtaskIds = [];
      await this.load(currentTab, true, currentTab);
      this.snackbarStore.showSuccessSnackbarMessage('Tasks reassigned successfully.');
    } catch {
      this.snackbarStore.showErrorSnackbarMessage(
        'There was a problem reassigning the tasks. Please try again or contact support.'
      );
    } finally {
      this.viewModel.isLoading = false;
    }
  };

  public saveDueDateChange = async (
    taskId: string | string[],
    dueDate: string,
    currentTab: TaskInternalExternal
  ) => {
    this.viewModel.isLoading = true;
    const taskIds = Array.isArray(taskId) ? taskId : [taskId];

    try {
      await this.repo.bulkUpdateTasks(this.viewModel.bankId, taskIds, undefined, undefined, dueDate);

      await this.load(currentTab, true, currentTab);

      this.snackbarStore.showSuccessSnackbarMessage('Due date updated successfully.');
    } catch {
      this.snackbarStore.showErrorSnackbarMessage(
        'There was a problem updating the due date. Please try again or contact support.'
      );
    } finally {
      this.viewModel.isLoading = false;
    }
  };

  public saveQuestionnaireReassignment = async (
    taskId: string,
    assigneeId: string,
    currentTab: TaskInternalExternal
  ) => {
    const assignee = this.viewModel.assignees.find((a) => a.id === assigneeId);
    if (!assignee) return;

    const assignedUserIds = assignee.type === TaskAssigneeType.USER ? [assignee.id] : undefined;
    const assignedGroupIds = assignee.type === TaskAssigneeType.GROUP ? [assignee.id] : undefined;

    this.viewModel.isLoading = true;

    try {
      await this.repo.bulkUpdateTasks(this.viewModel.bankId, [taskId], assignedUserIds, assignedGroupIds);

      await this.load(currentTab, true, currentTab);
      this.snackbarStore.showSuccessSnackbarMessage('Questionnaire reassigned successfully.');
    } catch {
      this.snackbarStore.showErrorSnackbarMessage(
        'There was a problem reassigning the questionnaire. Please try again or contact support.'
      );
    } finally {
      this.viewModel.isLoading = false;
    }
  };

  public resetPage = () => {
    this.updateViewModel({
      searchTerm: '',
      filters: [],
      internalFilteredTasks: [],
      externalFilteredTasks: [],
      tasks: [],
      showAutogen: false
    });
  };

  getInitializedFilters = (currentTab: string, initialLoad?: boolean, fromQuestionnaire?: boolean) => {
    const filters: FilterListItem[] = [];
    const assignee = new Map(this.viewModel.tasks.map((t) => [t.assignee, t.assignee]));
    const account = new Map(this.viewModel.tasks.map((t) => [t.account, t.account]));
    const createdBy = new Map(
      this.viewModel.tasks
        .filter((user) => user.createdBy !== 'Auto Generated')
        .map((t) => [t.createdBy, t.createdBy])
    );
    const status = 'status';
    const frequency = 'frequency';
    const openFilterObj = {
      label: getTaskStatusText(TaskStatus.OPEN),
      value: TaskStatus.OPEN,
      selected: (initialLoad && !fromQuestionnaire) ?? false,
      parentValue: status
    };
    const inProgressFilterObj = {
      label: getTaskStatusText(TaskStatus.PROCESSING),
      value: TaskStatus.PROCESSING,
      selected: (initialLoad && !fromQuestionnaire) ?? false,
      parentValue: status
    };
    const suppressedFilterObj = {
      label: getTaskStatusText(TaskStatus.SUPPRESSED),
      value: TaskStatus.SUPPRESSED,
      parentValue: status
    };
    const completedFilterObj = {
      label: getTaskStatusText(TaskStatus.COMPLETE),
      value: TaskStatus.COMPLETE,
      parentValue: status
    };
    if (currentTab === TaskInternalExternal.INTERNAL) {
      filters.push({
        label: 'Status',
        value: status,
        children: [openFilterObj, inProgressFilterObj, completedFilterObj]
      });
      filters.push({
        label: 'Assignee',
        value: 'assignee',
        children: [...assignee]
          .sort((a, b) => {
            return a[0] < b[0] ? -1 : 1;
          })
          .map(([label, value]) => {
            const filterLabel = label === '--' ? 'None' : label;
            const filterValue = value === '--' ? 'assignee_none' : value;

            return { label: filterLabel, value: filterValue, parentValue: 'assignee' };
          })
      });
      filters.push({
        label: 'Account',
        value: 'account',
        children: [...account]
          .sort((a, b) => {
            return a[0] < b[0] ? -1 : 1;
          })
          .map(([label, value]) => {
            const filterLabel = label === '--' ? 'None' : label;
            const filterValue = value === '--' ? 'account_none' : value;

            return { label: filterLabel, value: filterValue, parentValue: 'account' };
          })
      });
      filters.push({
        label: 'Created By',
        value: 'createdBy',
        children: [...createdBy]
          .sort((a, b) => {
            return a[0] < b[0] ? -1 : 1;
          })
          .map(([label, value]) => {
            const filterLabel = label === '--' ? 'Auto Created' : label;
            const filterValue = value === '--' ? 'autoCreated' : value;

            return { label: filterLabel, value: filterValue, parentValue: 'createdBy' };
          })
      });
    } else {
      filters.push({
        label: 'Status',
        value: status,
        children: [openFilterObj, inProgressFilterObj, suppressedFilterObj, completedFilterObj]
      });
      filters.push({
        label: 'Type',
        value: 'type',
        children: [
          { label: 'Task', value: TaskRowType.Task, parentValue: 'type' },
          { label: 'Questionnaire', value: TaskRowType.Questionnaire, parentValue: 'type' },
          { label: 'Questionnaire Revision', value: TaskRowType.QuestionnaireRevision, parentValue: 'type' }
        ]
      });
      filters.push({
        label: 'Frequency',
        value: frequency,
        children: [
          {
            label: 'Weekly',
            value: 'Weekly',
            parentValue: frequency
          },
          {
            label: 'Monthly',
            value: 'Monthly',
            parentValue: frequency
          },
          {
            label: 'Quarterly',
            value: 'Quarterly',
            parentValue: frequency
          },
          {
            label: 'Semi-Annually',
            value: 'Semi-Annually',
            parentValue: frequency
          },
          {
            label: 'Annually',
            value: 'Annually',
            parentValue: frequency
          },
          {
            label: 'One-Time',
            value: 'One-Time',
            parentValue: frequency
          }
        ]
      });
      filters.push({
        label: 'Created By',
        value: 'createdBy',
        children: [...createdBy]
          .sort((a, b) => {
            return a[0] < b[0] ? -1 : 1;
          })
          .map(([label, value]) => {
            const filterLabel = label === '--' ? '--' : label;
            const filterValue = value === '--' ? '--' : value;

            return { label: filterLabel, value: filterValue, parentValue: 'createdBy' };
          })
      });
    }

    if (fromQuestionnaire) {
      const preSelected: FilterListChild[] = this.appViewStore.tasksFilters[currentTab];
      preSelected.forEach((filter) => {
        const parent = filters.find((f) => f.value === filter.parentValue);
        if (parent) {
          const child = parent.children.find((c) => c.value === filter.value);
          if (child) {
            child.selected = true;
          }
        }
      });
    }
    return filters;
  };

  public toggleAutogen = (currentTab: TaskInternalExternal) => {
    this.viewModel.showAutogen = !this.viewModel.showAutogen;
    this.refilterResults(currentTab);
  };
}
